'use client';

import { MockUser } from '@/types';
import { useLanguage } from '@/context/LanguageContext';

interface CommentAvatarsProps {
  commentUserIds: number[];
  users: MockUser[];
  maxVisible?: number;
  size?: 'sm' | 'md';
}

export function CommentAvatars({ commentUserIds, users, maxVisible = 5, size = 'sm' }: CommentAvatarsProps) {
  const { t } = useLanguage();
  // Get unique user IDs
  const uniqueUserIds = Array.from(new Set(commentUserIds));
  const commenters = users.filter(user => uniqueUserIds.includes(user.id));
  const totalCount = commenters.length;

  if (totalCount === 0) {
    return null;
  }

  const visibleCommenters = commenters.slice(0, maxVisible);
  const remainingCount = totalCount - maxVisible;

  const avatarSize = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const fontSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center -space-x-2">
        {visibleCommenters.map((user, index) => (
          <div
            key={user.id}
            className={`${avatarSize} rounded-full border-2 border-white overflow-hidden flex-shrink-0`}
            style={{ zIndex: visibleCommenters.length - index }}
            title={user.displayName}
          >
            {user.avatarUrl ? (
              <img 
                src={user.avatarUrl} 
                alt={user.displayName} 
                className="w-full h-full object-contain"
              />
            ) : (
              <div
                className={`w-full h-full flex items-center justify-center text-white font-bold ${fontSize}`}
                style={{ backgroundColor: user.avatarColor }}
              >
                {user.initials}
              </div>
            )}
          </div>
        ))}
        {remainingCount > 0 && (
          <div
            className={`${avatarSize} rounded-full border-2 border-white bg-gray-300 flex items-center justify-center flex-shrink-0`}
            style={{ zIndex: 0 }}
            title={`${remainingCount} ${t.comments.more}`}
          >
            <span className={`text-gray-600 font-semibold ${fontSize}`}>
              +{remainingCount}
            </span>
          </div>
        )}
      </div>
      <span className="text-sm text-foreground-muted">
        {totalCount} {totalCount === 1 ? t.comments.comment : t.comments.comments}
      </span>
    </div>
  );
}


